import { SESSION_STORAGE_LAST_SITE } from 'src/common/constants';
import { logger } from 'src/logger';

/**
 * Remembers the site last opened in this tab, so a reload reopens it instead of the first scope the
 * user has access to.
 *
 * Stored in sessionStorage, like the rest of FMUI's client state, so it is scoped to a single tab:
 * operators with several windows open (often on different sites or regions) do not overwrite each
 * other's selection. It is cleared on sign-out (see signOut in util.ts) and does not carry over to a
 * new tab or a browser restart; for most users, who have one or two sites, that default is fine.
 */

/**
 * Returns the site last opened in this tab, but only if it is still one of the sites the user can
 * access now -- scopes can be revoked, and restoring a revoked site would make the page request data
 * the user is no longer allowed to see.
 *
 * @param allowedSites the sites the user currently has access to.
 * @returns the remembered site, or undefined if there is none or it is no longer allowed.
 */
export const loadLastSite = (allowedSites: string[]): string | undefined => {
    try {
        const saved = window.sessionStorage.getItem(SESSION_STORAGE_LAST_SITE);
        return saved && allowedSites.includes(saved) ? saved : undefined;
    } catch (e) {
        // Storage can throw when it is disabled (e.g. some private-browsing modes). Remembering the
        // site is a convenience, so fall back to the default rather than failing.
        logger.warn('Unable to read the last opened site from sessionStorage.', e as Error);
        return undefined;
    }
};

/**
 * Remembers the site just opened in this tab.
 *
 * @param site the site that was opened; ignored when undefined or empty.
 */
export const saveLastSite = (site: string | undefined): void => {
    if (!site) {
        return;
    }
    try {
        window.sessionStorage.setItem(SESSION_STORAGE_LAST_SITE, site);
    } catch (e) {
        logger.warn('Unable to save the last opened site to sessionStorage.', e as Error);
    }
};
